import { graphql } from '~/graphql/generated/gql';

export const learningFaqFragment = graphql(`
  fragment LearningFaqFields on LearningFaq {
    id
    question
    answer
    slug
    section
    order
    learningCampaignId
  }
`);

export const adminLearningFaqFragment = graphql(`
  fragment AdminLearningFaqFields on AdminLearningFaq {
    id
    question
    answer
    slug
    section
    order
    learningCampaignId
    createdAt
    updatedAt
  }
`);

export const learningCampaignMetaFragment = graphql(`
  fragment LearningCampaignMetaFields on LearningCampaignMeta {
    termsConditions
    blinkyOnboardingMessage
    skillQuestion
  }
`);

export const adminLearningCampaignMetaFragment = graphql(`
  fragment AdminLearningCampaignMetaFields on AdminLearningCampaignMeta {
    termsConditions
    blinkyOnboardingMessage
    skillQuestion
  }
`);

export const adminLearningCampaignFragment = graphql(`
  fragment AdminLearningCampaignFields on AdminLearningCampaign {
    id
    name
    rewardText
    hasPrize
    heroImageId
    heroImage {
      id
      path
      signedUrl
      mimeType
      size
      originalName
    }
    startDate
    endDate
    learningAssetCollectionId
    learningAssetCollection {
      id
      name
    }
    publishedAt
    createdAt
    updatedAt
    deletedAt
    meta {
      ...AdminLearningCampaignMetaFields
    }
    systemTag
    campaignSubtitle
    campaignDescription
    partnerId
    attributionLabel
    partner {
      ...AdminPartnerReferenceFields
    }
    entriesCount
    currentDraw {
      id
      candidatesCount
      confirmedCount
    }
  }
`);

export const learningCampaignCollectionOverviewFragment = graphql(`
  fragment LearningCampaignCollectionOverviewFields on LearningCampaign {
    id
    name
    rewardText
    hasPrize
    attributionLabel
    campaignSubtitle
    campaignDescription
    meta {
      skillQuestion
    }
    heroImage {
      ...PublicFileFields
    }
    startDate
    endDate
    publishedAt
    isUserEligible
    userEligibilityChecks {
      isCampaignValid
      isLearningCollectionCompleted
      isAccountDetailsCompleted
      hasUserEnteredCampaignPrize
      ineligibilityReason
    }
    currentUserLearningCampaignEntries {
      ...UserLearningCampaignEntryFields
    }
    partner {
      ...PartnerReferenceFields
    }
  }
`);

export const userLearningCampaignEntryFragment = graphql(`
  fragment UserLearningCampaignEntryFields on UserLearningCampaignEntry {
    id
    skillQuestion
    skillAnswer
  }
`);

export const adminLearningCampaignWithFaqsFragment = graphql(`
  fragment AdminLearningCampaignWithFaqsFields on AdminLearningCampaign {
    ...AdminLearningCampaignFields
    learningFaqs {
      ...AdminLearningFaqFields
    }
  }
`);

export const fetchAdminCampaign = graphql(`
  query fetchAdminCampaign($id: ID!) {
    campaign(id: $id) {
      ...AdminLearningCampaignWithFaqsFields
    }
  }
`);

export const fetchAdminCampaignReport = graphql(`
  query fetchAdminCampaignReport($id: ID!) {
    campaign(id: $id) {
      id
      name
      startDate
      endDate
      publishedAt
      rewardText
      hasPrize
      entriesCount
      learningAssetCollection {
        id
        name
      }
      stats {
        totalNewUsers
        newUsersStarted
        existingUsersStarted
        newUsersCompleted
        existingUsersCompleted
        totalLaCompletions
        schoolNewUsersStarted
        schoolExistingUsersStarted
        schoolNewUsersCompleted
        schoolExistingUsersCompleted
        schoolTotalLaCompletions
      }
      currentDraw {
        ...PrizeDrawFields
      }
    }
  }
`);

export const fetchAdminCampaignEntrants = graphql(`
  query fetchAdminCampaignEntrants(
    $campaignId: ID!
    $page: Int
    $first: Int
    $filters: CampaignEntrantFilters
  ) {
    campaignEntrantsList(
      campaignId: $campaignId
      page: $page
      first: $first
      filters: $filters
    ) {
      data {
        isChannelCompleted
        entry {
          id
          skillAnswer
          createdAt
        }
        user {
          ...CohortUserFields
        }
      }
      paginatorInfo {
        currentPage
        lastPage
        total
        hasMorePages
      }
    }
  }
`);

export const fetchLearningCampaignTermsConditions = graphql(`
  query fetchLearningCampaignTermsConditions($id: ID!) {
    learningCampaign(id: $id) {
      learningCampaign {
        ...LearningCampaignCollectionOverviewFields
        meta {
          ...LearningCampaignMetaFields
        }
      }
    }
  }
`);

export const fetchAdminCampaignOptions = graphql(`
  query fetchAdminCampaignOptions($first: Int) {
    campaignsList(first: $first, orderBy: [{ column: NAME, order: ASC }]) {
      data {
        id
        name
        learningFaqs {
          id
        }
      }
    }
  }
`);

export const fetchCampaignAttributionLabels = graphql(`
  query fetchCampaignAttributionLabels {
    campaignAttributionLabels {
      id
      label
      isStarter
      isInUse
    }
  }
`);

export const createCampaignAttributionLabelMutation = graphql(`
  mutation createCampaignAttributionLabel($input: CreateCampaignAttributionLabelInput!) {
    createCampaignAttributionLabel(input: $input) {
      id
      label
      isStarter
      isInUse
    }
  }
`);

export const deleteCampaignAttributionLabelMutation = graphql(`
  mutation deleteCampaignAttributionLabel($input: DeleteCampaignAttributionLabelInput!) {
    deleteCampaignAttributionLabel(input: $input)
  }
`);

export const upsertLearningCampaignMutation = graphql(`
  mutation upsertLearningCampaign($input: UpsertLearningCampaignInput!) {
    upsertLearningCampaign(input: $input) {
      learningCampaign {
        ...AdminLearningCampaignFields
      }
    }
  }
`);

export const deleteLearningCampaignMutation = graphql(`
  mutation deleteLearningCampaign($input: DeleteLearningCampaignInput!) {
    deleteLearningCampaign(input: $input) {
      success
    }
  }
`);

export const bulkDeleteLearningCampaignsMutation = graphql(`
  mutation bulkDeleteLearningCampaigns($input: BulkDeleteLearningCampaignsInput!) {
    bulkDeleteLearningCampaigns(input: $input) {
      success
      successCount
      failureCount
    }
  }
`);

export const publishLearningCampaignMutation = graphql(`
  mutation publishLearningCampaign($input: PublishLearningCampaignInput!) {
    publishLearningCampaign(input: $input) {
      success
      learningCampaign {
        ...AdminLearningCampaignFields
      }
    }
  }
`);

export const unpublishLearningCampaignMutation = graphql(`
  mutation unpublishLearningCampaign($input: UnpublishLearningCampaignInput!) {
    unpublishLearningCampaign(input: $input) {
      success
      learningCampaign {
        ...AdminLearningCampaignFields
      }
    }
  }
`);

export const fetchAllAdminCampaigns = graphql(`
  query fetchAllAdminCampaigns(
    $page: Int
    $first: Int
    $filters: LearningCampaignFilters
    $orderBy: [LearningCampaignOrder!]
  ) {
    campaignsList(
      page: $page
      first: $first
      filters: $filters
      orderBy: $orderBy
    ) {
      data {
        ...AdminLearningCampaignFields
      }
      paginatorInfo {
        currentPage
        lastPage
        total
        hasMorePages
      }
    }
  }
`);

export const fetchAdminCampaignsChartStats = graphql(`
  query fetchAdminCampaignsChartStats(
    $page: Int
    $first: Int
    $filters: LearningCampaignFilters
    $orderBy: [LearningCampaignOrder!]
  ) {
    campaignsList(
      page: $page
      first: $first
      filters: $filters
      orderBy: $orderBy
    ) {
      data {
        id
        name
        startDate
        stats {
          totalNewUsers
          newUsersStarted
          existingUsersStarted
          newUsersCompleted
          existingUsersCompleted
        }
      }
      paginatorInfo {
        total
      }
    }
  }
`);

export const fetchLearningFaqsBySection = graphql(`
  query fetchLearningFaqsBySection($first: Int, $orderBy: [LearningFaqOrder!], $sections: [FaqSection]) {
    learningFaqs(first: $first, filters: { sections: $sections }, orderBy: $orderBy) {
      data {
        ...LearningFaqFields
      }
    }
  }
`);

export const fetchPublicFaqSections = graphql(`
  query fetchPublicFaqSections {
    publicFaqSections
  }
`);

// Fragment-free variant for guest surfaces on the schools clusters: the guest
// GraphQL gate's SelectionAllowlist fails closed on fragment spreads and only
// admits these leaf fields (EnsureSchoolsClusterAuthenticated::ALLOWED_SELECTIONS).
export const fetchPublicLearningFaqs = graphql(`
  query fetchPublicLearningFaqs($first: Int, $orderBy: [LearningFaqOrder!], $sections: [FaqSection]) {
    learningFaqs(first: $first, filters: { sections: $sections }, orderBy: $orderBy) {
      data {
        id
        question
        answer
      }
    }
  }
`);

export const upsertLearningFaqsBatchMutation = graphql(`
  mutation upsertLearningFaqsBatch($input: UpsertLearningFaqsBatchInput!) {
    upsertLearningFaqsBatch(input: $input) {
      learningFaqs {
        ...LearningFaqFields
      }
      count
    }
  }
`);

export const deleteLearningFaqMutation = graphql(`
  mutation deleteLearningFaq($input: DeleteLearningFaqInput!) {
    deleteLearningFaq(input: $input) {
      success
    }
  }
`);

export const createUserLearningCampaignEntryMutation = graphql(`
  mutation createUserLearningCampaignEntry($input: CreateUserLearningCampaignEntryInput!) {
    createUserLearningCampaignEntry(input: $input) {
      userLearningCampaignEntry {
        ...UserLearningCampaignEntryFields
        learningCampaign {
          id
          isUserEligible
          userEligibilityChecks {
            isCampaignValid
            isLearningCollectionCompleted
            isAccountDetailsCompleted
            hasUserEnteredCampaignPrize
            ineligibilityReason
          }
          currentUserLearningCampaignEntries {
            ...UserLearningCampaignEntryFields
          }
        }
      }
    }
  }
`);

export const unifiedLearningCampaignFragment = graphql(`
  fragment UnifiedLearningCampaignFields on LearningCampaign {
    id
    name
    heroImage {
      ...PublicFileFields
    }
    startDate
    endDate
    rewardText
    hasPrize
    attributionLabel
    campaignSubtitle
    campaignDescription
    systemTag
    meta {
      skillQuestion
    }
    isUserEligible
    userEligibilityChecks {
      isCampaignValid
      isLearningCollectionCompleted
      isAccountDetailsCompleted
      hasUserEnteredCampaignPrize
      ineligibilityReason
    }
    learningAssetCollection {
      ...LearningAssetCollectionExploreFields
    }
    partner {
      ...PartnerReferenceFields
    }
    currentUserLearningCampaignEntries {
      ...UserLearningCampaignEntryFields
    }
  }
`);

export const fetchActiveLearningCampaigns = graphql(`
  query fetchActiveLearningCampaigns {
    activeLearningCampaigns {
      campaigns {
        learningCampaign {
          ...UnifiedLearningCampaignFields
        }
        faqs {
          ...LearningFaqsField
        }
      }
    }
  }
`);
